import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface SignupLayoutProps {
  step: 1 | 2 | 3 | 4 | 5 | 6;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Hide the step dots on the handoff card so kids don't think they need to navigate. */
  hideStepDots?: boolean;
}

/**
 * Shared chrome for the 6-screen sign-up flow.
 *
 * Mobile-first card: 100% width below 480px, capped at ~480px on desktop.
 * Step dots in the top center for orientation; back link top-left for screens 2+.
 *
 * Spec ref §11.3.3 — keep visual weight low; the parent should feel like
 * they're doing 3 small steps, not "filling out a form."
 */
export function SignupLayout({
  step,
  title,
  subtitle,
  children,
  hideStepDots,
}: SignupLayoutProps): JSX.Element {
  return (
    <div className="min-h-dvh bg-amber-50 px-4 py-6 sm:py-12">
      <div className="mx-auto max-w-md">
        <header className="mb-6 flex items-center justify-between">
          {step > 1 ? (
            <Link
              to={`/signup/${step - 1}`}
              className="text-sm text-slate-600 underline-offset-2 hover:underline"
              aria-label="Back"
            >
              ← Back
            </Link>
          ) : (
            <span aria-hidden="true" />
          )}

          {!hideStepDots && (
            <div className="flex gap-1.5" aria-label={`Step ${step} of 6`}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full ${
                    i <= step ? 'bg-amber-500' : 'bg-amber-200'
                  }`}
                />
              ))}
            </div>
          )}

          <span aria-hidden="true" />
        </header>

        <main className="rounded-3xl bg-white p-6 shadow-md sm:p-8">
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1>
          {subtitle && <p className="mt-2 text-base text-slate-600">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
